// A BARRA DE NAVEGAÇÃO.
//
// Três coisas estavam erradas aqui ao mesmo tempo, e as três apareceram só quando eu finalmente
// OLHEI as telas — nenhuma delas quebra o build, e nenhuma delas aparece em typecheck.
//
//   1. CONTRASTE. O CSS pintava `color: white` cravado, herdado da versão clássica, onde o campo
//      é um verde saturado. No tema da marca o campo é um verde pálido: branco sobre ele fica em
//      ~1.4:1. Era o primeiro elemento de toda tela, e estava ilegível em todas elas.
//
//   2. SEMÂNTICA. Cada item era um `<span onClick>`. Um `<span>` não é alcançável por Tab, não é
//      anunciado como link, não abre em aba nova, não tem estado de foco. Numa plataforma de
//      escola isso não é detalhe: é a diferença entre a criança conseguir usar e não conseguir.
//      Agora são `<Link>`, que renderizam `<a href>` de verdade.
//
//   3. ONDE EU ESTOU. A barra não dizia. Sete destinos, nenhuma marca de qual está aberto.
//      `aria-current="page"` responde isso para o leitor de tela, e o sublinhado responde para
//      quem enxerga — a mesma informação por dois canais, não a cor sozinha.
//
// SAIR continua sendo `<button>`, e é o único: sair não é navegar, é executar. Marcar de link o
// que muda estado é mentir sobre o que o clique faz.

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { podeSair } from "../pages/meus-quadrinhos/saida-sem-salvar";
import "./nav.scss";
import { useGlobalState } from "../global-state-context";
import Firebase from "../api/firebase.class";
import assinatura from "../assets/marca/hqlab-assinatura.png";
import { CARTILHAS } from "./cartilhas";

// O `handlers` MORREU, e a razao e estrutural.
//
// Ele existia para uma coisa so: dentro do `/dashboard`, "Meus quadrinhos" e "Minha conta" nao
// eram destinos — eram estados do mesmo componente. Um `<Link>` para a rota em que voce ja esta
// nao faz nada, entao o menu precisava de um gancho para pedir ao dono do estado que mudasse.
//
// Com o acervo quebrado em quatro rotas (T2.45), cada destino e um endereco de verdade e o
// `<Link>` basta. E melhor que bastar: o caminho interceptado quebrava abrir em aba nova e
// ctrl+clique, porque `preventDefault` acontecia antes de o navegador ver a intencao.

// ── A LISTA DO CABEÇALHO, DEPOIS DO T2 ──────────────────────────────────────────────────────
//
// Quatro pontos decidem esta lista, e eles se somam numa régua só — DELE:
//
//   T2.28  "a gente tá querendo apostar mais coisas que são FUNCIONAIS. Ninguém quer, tipo assim,
//           logou, quer ficar procurando saber quem somos, objetivo, parceiro."
//   T2.34  "em cima manter as coisas que a pessoa de fato vai querer ter acesso de forma mais
//           rápida, realmente as coisas que vai ficar usando."
//   T2.89  "a gente falou que tinha que ter em cima, no cabeçalho, uma parte de CRIAR."
//   T2.105 "configuração para mim tá nada a ver. Tanto o termo quanto essa página existir."
//
// **Frequência de uso decide altura na página.** O que se acessa uma vez desceu para o rodapé
// (`components/rodape.tsx`), onde continua alcançável por um link — que é o T2.29 na letra: "se eu
// quiser acessar a rota, tem um botãozinho, ela consegue".
//
// ── AS DUAS DECISÕES QUE O OPERADOR ME DELEGOU EM 2026-09-12 ────────────────────────────────
//
// Ele deu autonomia para resolver as dúvidas que estavam travando o pedido. As duas que caem
// aqui estão em `DECISOES-COM-AUTONOMIA.md`, com o argumento inteiro:
//
//   D4 — "o cabeçalho fica com quatro itens ou três?" Aos 06:38 ele lista quatro, incluindo
//        "configuração"; aos 24:43 decide que configurações não deve existir. Dezoito minutos
//        entre as duas e ele não voltou à lista. **Decidi pela fala posterior**, e o cabeçalho
//        ficou com três mais o "criar".
//
//   D3 — o nome da área que junta minha conta e configurações. **"Minha conta"**, que é o que ela
//        propôs e ele aceitou na própria fala ("Tá, cabe").
//
// ── O INSTITUCIONAL SÓ SAI DEPOIS DO LOGIN, E ISSO É A LETRA DELE ───────────────────────────
//
// T2.33: "esse quem somos, objetivos, parceiros já está no rodapé. Podia permanecer lá DEPOIS QUE
// A PESSOA FIZER LOGIN de fato." Quem chega deslogado está justamente decidindo se confia no
// produto, e para essa pessoa "quem somos" é funcional. Depois do login, não é mais.
//
// (O T2.27 sai com "logada" e "deslogada" trocadas na transcrição; o dossiê já marca isso, e o
// T2.33 do mesmo áudio diz a mesma coisa na ordem certa.)

/** `externo`: o destino é outro site. Ver as cartilhas, logo abaixo. */
type Destino = { rotulo: string; para: string; externo?: boolean };

/**
 * Onde a criança vai toda hora. Vale logado ou não.
 *
 * AS CARTILHAS entram aqui, para logado e deslogado, porque são a ajuda de quem está usando: quem
 * travou no editor precisa achar o passo a passo sem descer até o rodapé. O endereço está em
 * `cartilhas.ts`.
 *
 * Viram `<a href>` e não `<Link>`: o destino é outro site, e o roteador não sabe navegar até ele.
 * Abrem em aba nova para não tirar ninguém da tela em que está — e por isso não passam pela
 * pergunta de saída sem salvar: o editor continua aberto na aba de origem.
 */
const SEMPRE: Destino[] = [
  { rotulo: "Biblioteca", para: "/biblioteca" },
  { ...CARTILHAS, externo: true },
];

/** Institucional: em cima só para quem ainda não entrou. Ver o bloco acima. */
const INSTITUCIONAL: Destino[] = [
  { rotulo: "Quem somos", para: "/quem-somos" },
  { rotulo: "Objetivos", para: "/objetivos" },
  { rotulo: "Parceiros", para: "/parceiros" },
];

const DESLOGADO: Destino[] = [
  { rotulo: "Entrar", para: "/login" },
  { rotulo: "Criar conta", para: "/cadastro" },
];

const LOGADO: Destino[] = [
  { rotulo: "Meus quadrinhos", para: "/meus-quadrinhos" },
  // "Configurações" SAIU. Ver D3/D4 acima e `pages/settings` — a rota continua existindo e
  // redireciona, porque endereço que some vira página em branco neste roteador.
  { rotulo: "Minha conta", para: "/minha-conta" },
];

/**
 * AS DUAS FORMAS DE CRIAR, E OS NOMES DELAS.
 *
 * T2.32 é uma pergunta: "criar um quadrinho, escrever, deixar montar. QUAL A DIFERENÇA desse
 * criar um quadrinho, escrever e deixar montar?"
 *
 * E a resposta está no bloco seguinte do mesmo áudio, `[A3-P22]`, que não virou ponto. Ela deduz
 * sozinha, olhando a tela, em dez segundos:
 *
 *   "esse escrever, deixar montar, você vai montar a TIRINHA TODA, você vai botar o texto tudo,
 *    tudo de vez, né? E O OUTRO VOCÊ VAI PAINEL POR PAINEL."
 *
 * Então a diferença existe e o texto da tela é que não a comunicava. Os rótulos abaixo são a
 * frase dela, quase sem edição — não é interpretação minha, é transcrição.
 *
 * E a causa estava medida: a home batizava os MESMOS dois destinos cinco vezes (três cartões de
 * etapa e dois botões de produto), e "Escreva a cena" e "Escrever e deixar montar" eram a mesma
 * rota com nomes diferentes na mesma tela. Não é que os nomes fossem ruins — é que havia cinco.
 */
export const FORMAS_DE_CRIAR: { rotulo: string; explica: string; para: string }[] = [
  {
    rotulo: "Escrever a história toda",
    explica: "Você escreve, e o HQ-Lab monta os quadros de uma vez.",
    para: "/roteirizar",
  },
  {
    rotulo: "Montar quadro por quadro",
    explica: "Você escolhe o tamanho da página e monta painel por painel.",
    para: "/meus-quadrinhos/novo",
  },
];

function Nav() {
  const navigate = useNavigate();
  const local = useLocation();
  const globalState = useGlobalState();
  const isLogged = !!globalState?.appUser;
  const [aberto, setAberto] = useState(false);

  // Trocar de rota fecha o menu do celular. Sem isto ele fica aberto por cima da tela nova, e a
  // criança precisa descobrir sozinha que tem que tocar no ☰ de novo para ver o que pediu.
  useEffect(() => setAberto(false), [local.pathname]);

  const [criarAberto, setCriarAberto] = useState(false);

  /**
   * T8.15 · O AVISO DE QUE A SAÍDA FALHOU, no menu, onde o botão está.
   *
   * Era `alert()`. E aqui a falha importa mais do que parece: se sair não deu certo, **a sessão
   * continua aberta** — num computador de escola que a próxima criança vai usar. Uma caixa do
   * navegador que se fecha no primeiro OK não é onde essa frase deve morar.
   */
  const [erroAoSair, setErroAoSair] = useState<string | null>(null);

  /**
   * T2.44 · PERGUNTA ANTES DE LEVAR A PESSOA PARA FORA.
   *
   * `preventDefault` só quando a resposta é não: assim o link segue sendo um link em todos os outros
   * casos — e em todas as telas que não registram guarda nenhuma, que são todas menos o editor.
   */
  function perguntarAntesDeSair(e: React.MouseEvent) {
    if (!podeSair()) e.preventDefault();
  }
  useEffect(() => setCriarAberto(false), [local.pathname]);

  const destinos = isLogged
    ? [...SEMPRE, ...LOGADO]
    : [...SEMPRE, ...INSTITUCIONAL, ...DESLOGADO];

  return (
    <nav className="nav-container" aria-label="Navegação principal">
      {/* ── A MARCA DE VERDADE, E ELA ERA TEXTO ────────────────────────────────────────
          T2.5: "o fato da gente ta usando um logo que NAO E O LOGO CORRETO."
          T2.8: "esse logo da HQ Lab ta PEQUENO, nao da, nao ta ganhando destaque na coisa."
          T2.52: "falta o simbolo da HQ-Lab no topo, e o simbolo do React."

          Medido em 2026-09-12: a barra mostrava as sete letras "HQ-LAB" em fonte de display, e
          nao a marca. E os tres icones de `public/` eram os do Create React App, byte a byte
          intocados desde 08/09 — o tamanho de cada um batia com o padrao do CRA.

          A assinatura vem de `especificacao/marca/manual/manual-hqlab-04.png`, recortada por
          conteudo. O `alt` e o nome do produto e nao "logo do HQ-Lab": para quem usa leitor de
          tela, este link leva ao inicio, e "logo" descreve o arquivo, nao o destino.

          A ALTURA DA IMAGEM E A RESPOSTA AO T2.8, e ela e maior que a caixa do texto que estava
          aqui: 38px contra os ~26px da linha de display anterior. "Ganhar destaque" com medida. */}
      {/* T2.44 · A PERGUNTA ANTES DE SAIR, EM TODO LINK DESTE MENU.
          Medido em 27/09: com alteração registrada no editor, clicar em "Meus quadrinhos" **não
          perguntava nada** e saía. A guarda existia numa das três portas de saída — a do botão de
          voltar do próprio editor. Esta é a segunda; a terceira é o `beforeunload`.
          O porquê de `window.confirm` aqui está em `saida-sem-salvar.ts`: sem bloqueio de navegação
          no React Router desta versão, a decisão tem de ser síncrona. */}
      <Link
        className="nav-marca nav-marca-imagem"
        to="/"
        aria-label="HQ-Lab — inicio"
        onClick={perguntarAntesDeSair}
      >
        <img src={assinatura} alt="HQ-Lab" />
      </Link>

      <button
        type="button"
        className="menu-toggle"
        aria-expanded={aberto}
        aria-controls="nav-links"
        onClick={() => setAberto((x) => !x)}
      >
        <span className="visualmente-oculto">
          {aberto ? "Fechar menu" : "Abrir menu"}
        </span>
        <span aria-hidden="true">☰</span>
      </button>

      <div id="nav-links" className={`nav-links ${aberto ? "open" : ""}`}>
        {/* ── "CRIAR", O ITEM QUE O T2.89 PEDE ────────────────────────────────────────────
            "Se tem duas formas diferentes de você criar, elas têm que aparecer quando você
             estiver na ferramenta criar" (T2.91), e "só tá aparecendo essa opção de roteiro na
             home" (T2.90). Medido em 2026-09-12: `/roteirizar` não estava em lista nenhuma do
             menu — a única porta era um botão da home, e quem saísse da home perdia o caminho.

            É `<button aria-expanded>` e não link: abrir uma lista é mudar estado, não navegar.
            Os dois destinos dentro são `<Link>`, porque aqueles navegam. */}
        {isLogged && (
          <div className="nav-criar">
            <button
              type="button"
              className={`nav-link nav-criar-botao ${criarAberto ? "aberto" : ""}`}
              aria-expanded={criarAberto}
              aria-controls="nav-criar-menu"
              onClick={() => setCriarAberto((x) => !x)}
            >
              Criar <span aria-hidden="true">{criarAberto ? "▴" : "▾"}</span>
            </button>

            {criarAberto && (
              <div id="nav-criar-menu" className="nav-criar-menu" role="group" aria-label="Como criar">
                {FORMAS_DE_CRIAR.map((f) => (
                  <Link
                    key={f.para}
                    to={f.para}
                    className="nav-criar-opcao"
                    onClick={perguntarAntesDeSair}
                  >
                    <strong>{f.rotulo}</strong>
                    <small>{f.explica}</small>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {destinos.map(({ rotulo, para, externo }) => {
          if (externo) {
            return (
              <a
                key={para}
                href={para}
                className="nav-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {rotulo}
                <span className="visualmente-oculto"> (abre em outra aba)</span>
              </a>
            );
          }
          const aqui = local.pathname === para;
          return (
            <Link
              key={para}
              to={para}
              className={`nav-link ${aqui ? "nav-link-atual" : ""}`}
              aria-current={aqui ? "page" : undefined}
              onClick={perguntarAntesDeSair}
            >
              {rotulo}
            </Link>
          );
        })}

        {isLogged && (
          <button
            type="button"
            className="nav-sair"
            onClick={async () => {
              // T1.35 do pedido: "não consigo sair da minha conta".
              //
              // Antes, isto era `navigate("/"); Firebase.logOut();` — a navegação acontecia
              // ANTES da saída, e a promessa não era aguardada nem tratada. Duas consequências:
              // se o signOut falhasse, falhava em silêncio e a sessão continuava aberta; e
              // navegar antes deixava o estado global re-hidratar de uma sessão ainda válida,
              // que é a "conta que não sai" e o "cenário chapado ao trocar de conta".
              //
              // Agora: sai primeiro, navega depois, e falha é dita ao usuário.
              try {
                await Firebase.logOut();
              } catch (err) {
                console.error("[nav] falha ao sair da conta", err);
                // T8.15 · o `alert()` saiu. Aqui o aviso vai no proprio menu, que e onde o botao
                // de sair esta — e nao numa caixa que tampa a tela toda para dizer uma frase.
                setErroAoSair("Não deu para sair da sua conta agora. Tente de novo em instantes.");
                return;
              }
              navigate("/");
            }}
          >
            Sair
          </button>
        )}

        {erroAoSair && (
          <p className="nav-erro-ao-sair" role="alert">
            {erroAoSair}
          </p>
        )}
      </div>
    </nav>
  );
}

export default Nav;
